import { ArrowBackIosNew, ArrowForwardIos, StarOutlined } from '@mui/icons-material';
import {
  Box,
  Container,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useRef } from 'react';
import { PropertyCard, PropertyCardSkeleton } from '../../components/PropertyCard';

const FEATURED_COUNT = 6;

const FeaturedProperties = ({ properties, loading, categories }: any) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -340 : 340;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.paper', py: 4 }}>
        <Container maxWidth="lg">
          <Skeleton variant="text" width={200} height={36} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2.5, overflow: 'hidden' }}>
            {[...Array(isMobile ? 1 : 3)].map((_, i) => (
              <Box key={i} sx={{ minWidth: isMobile ? '100%' : 320, flexShrink: 0 }}>
                <PropertyCardSkeleton />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    );
  }

  if (!properties || properties.length === 0) return null;

  return (
    <Box sx={{ bgcolor: 'background.paper', py: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarOutlined sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700} color="text.primary">
              عقارات مميزة
            </Typography>
          </Box>
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => scroll('right')}
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ArrowForwardIos sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => scroll('left')}
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ArrowBackIosNew sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 2.5,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            pb: 1,
            px: 0.5,
          }}
        >
          {properties.slice(0, FEATURED_COUNT).map((property: any) => (
            <motion.div
              key={property.id}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              style={{ flexShrink: 0, scrollSnapAlign: 'start' }}
            >
              <Box
                sx={{
                  width: isMobile ? 'calc(100vw - 64px)' : 320,
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  boxShadow: `0 0 12px -2px ${theme.palette.primary.main}40`,
                  transition: 'box-shadow 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 0 20px -2px ${theme.palette.primary.main}70`,
                  },
                  '& .MuiCard-root': {
                    border: 'none',
                    borderRadius: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    zIndex: 3,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    px: 1.2,
                    py: 0.3,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <StarOutlined sx={{ fontSize: 14 }} />
                  مميز
                </Box>
                <PropertyCard property={property} categories={categories} />
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturedProperties;
